// =====================================================================
// Procedure colors — single source of truth for any chart, palette, or
// summary that paints / annotates plan items.
//
// Each category has a small palette of hues in the same color family
// (e.g. Restorative = five shades of blue) so different procedures
// within a category get visually distinct colors while still reading
// as "all restorative work." Per-procedure color is derived
// deterministically from the procedure's stable_id / id / name so
// the same procedure always paints the same hue, across charts,
// palette swatches, and summaries.
//
// A clinic-customised color (procedures.color_hex) takes precedence
// over the derived default — when Settings → Services lets a clinic
// pick their own palette, that value is read here.
// =====================================================================

const DEFAULT_COLOR = "#0d9488";

// Hues per family. Procedures sorted alphabetically inside each
// category get hues by position, so a category reads as a clean ramp
// instead of random swatches. Most families use DARK → LIGHT; category
// ramps can opt into their clinically preferred reading order.
export const CATEGORY_HUES = {
  Restorative:    ["#1e3a8a", "#1d4ed8", "#3b82f6", "#60a5fa", "#bfdbfe"], // blues
  Preventive:     ["#134e4a", "#0f766e", "#0d9488", "#2dd4bf", "#99f6e4"], // teals
  Diagnostic:     ["#1e293b", "#475569", "#64748b", "#94a3b8", "#cbd5e1"], // slate
  Endodontic:     ["#4c1d95", "#6d28d9", "#8b5cf6", "#a78bfa", "#ddd6fe"], // violets
  Periodontal:    ["#064e3b", "#047857", "#059669", "#34d399", "#a7f3d0"], // greens
  Periodontic:    ["#064e3b", "#047857", "#059669", "#34d399", "#a7f3d0"], // alias
  Prosthodontic:  ["#fef3c7", "#fde68a", "#fcd34d", "#fbbf24", "#f59e0b", "#d97706", "#b45309", "#92400e"], // gold/amber, light → dark
  Orthodontic:    ["#831843", "#be185d", "#ec4899", "#f472b6", "#fbcfe8"], // pinks
  Pediatric:      ["#7c2d12", "#c2410c", "#f59e0b", "#fb923c", "#fed7aa"], // oranges
  "Oral Surgery": ["#7f1d1d", "#991b1b", "#dc2626", "#f87171", "#fecaca"], // reds
  Cosmetic:       ["#581c87", "#7e22ce", "#a855f7", "#c084fc", "#e9d5ff"], // purples
  Implant:        ["#155e75", "#0e7490", "#06b6d4", "#22d3ee", "#a5f3fc"], // cyans
  Emergency:      ["#4c0519", "#9f1239", "#e11d48", "#fb7185", "#fecdd3"], // rose
};

// Backwards-compat: keep the single-color-per-category map exported
// for any caller that prefers a representative swatch (legends, etc.).
// Picks the middle hue of each family.
export const PROCEDURE_CATEGORY_COLORS = Object.fromEntries(
  Object.entries(CATEGORY_HUES).map(([cat, hues]) => [cat, hues[2]])
);

// Build a stable id→color map from a procedures list. Sorting by
// name within each category and assigning hues position-based gives
// a smooth darkness ramp per category — Cameron's "more linear hue"
// ask. Procedures with a clinic-custom color_hex (Settings →
// Services, future PR) keep their own value regardless.
export function buildProcedureColorMap(procedures = []) {
  const byCat = new Map();
  for (const p of procedures) {
    if (!p || !p.id) continue;
    const cat = p.category || "Other";
    if (!byCat.has(cat)) byCat.set(cat, []);
    byCat.get(cat).push(p);
  }
  const out = {};
  for (const [cat, items] of byCat.entries()) {
    items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    const hues = CATEGORY_HUES[cat] || [DEFAULT_COLOR];
    items.forEach((p, idx) => {
      out[p.id] = p.color_hex || hues[idx % hues.length];
    });
  }
  return out;
}

// procedureColor — accepts either:
//   * a category string (legacy callers like OverviewChart) → returns
//     the family's middle hue.
//   * a procedure-like object {id, stable_id, category, color_hex, name}
//     plus an optional colorMap (from buildProcedureColorMap).
//     Returns the clinic-custom color if set, otherwise the position-
//     based hue from the colorMap, otherwise falls back to the family's
//     middle hue. The colorMap path is what gives us the dark→light
//     ramp; without it we still return a stable color but lose the
//     intra-category ordering.
export function procedureColor(arg, colorMap = null) {
  if (!arg) return DEFAULT_COLOR;
  if (typeof arg === "string") {
    const hues = CATEGORY_HUES[arg];
    return hues ? hues[Math.floor(hues.length / 2)] : DEFAULT_COLOR;
  }
  if (arg.color_hex) return arg.color_hex;
  if (colorMap && colorMap[arg.id]) return colorMap[arg.id];
  const hues = CATEGORY_HUES[arg.category];
  return hues ? hues[Math.floor(hues.length / 2)] : DEFAULT_COLOR;
}

// Multi-tooth procedure styling — distinct color + short clinical
// label per kind so a dentist can decode the chart at a glance:
//   BRIDGE   — red    (most common multi-unit; matches Cameron's
//                       hand-annotated reference)
//   SPLINT   — orange (perio splint; usually anteriors)
//   RPD      — emerald (Removable Partial Denture, PH market term)
//   DENTURE  — violet  (full / complete denture)
//   CROWN    — cyan    (multi-unit crown work, rare)
// Splint is stored as multi_tooth_kind='bridge' in the catalog
// (migration 20260606000001 backfill collapses bridge|splint), so we
// inspect procedure_name first to peel splints back out.
export const MULTI_TOOTH_KIND_STYLES = {
  bridge:          { label: "BRIDGE",  color: "#b91c1c" },
  splint:          { label: "SPLINT",  color: "#c2410c" },
  partial_denture: { label: "RPD",     color: "#047857" },
  full_denture:    { label: "DENTURE", color: "#6d28d9" },
  crown_unit:      { label: "CROWN",   color: "#0e7490" },
};

const FALLBACK_MULTI_COLOR = "#475569";

export function classifyMultiTooth({ multi_tooth_kind, procedure_name } = {}) {
  const name = String(procedure_name || "").toLowerCase();
  if (name.includes("splint")) return { kind: "splint", ...MULTI_TOOTH_KIND_STYLES.splint };
  let kind = multi_tooth_kind && MULTI_TOOTH_KIND_STYLES[multi_tooth_kind]
    ? multi_tooth_kind
    : null;
  if (!kind) {
    if (name.includes("bridge"))                                      kind = "bridge";
    else if (name.includes("rpd") || name.includes("removable partial")) kind = "partial_denture";
    else if (name.includes("denture") || name.includes("complete"))   kind = "full_denture";
  }
  if (kind && MULTI_TOOTH_KIND_STYLES[kind]) {
    return { kind, ...MULTI_TOOTH_KIND_STYLES[kind] };
  }
  // Unknown multi-tooth procedure — fall back to first word of the
  // procedure name so the dentist still gets a readable label.
  const fallbackLabel = String(procedure_name || "GROUP")
    .toUpperCase()
    .split(/\s+/)[0]
    .slice(0, 12) || "GROUP";
  return { kind: "other", label: fallbackLabel, color: FALLBACK_MULTI_COLOR };
}
